import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { fetchProducts } from "@/lib/products";
import { ProductCard } from "@/components/site/ProductCard";
import { MonogramLoader } from "@/components/site/MonogramLoader";
import { BackButton } from "@/components/site/BackButton";

type Props = {
  eyebrow: string;
  title: string;
  subtitle: string;
  filterTag?: string;
  filterCategory?: string;
  subFilters?: { label: string; value: string }[];
  subFilterKey?: "subcategory" | "tag";
};

/**
 * SectionPage — luxury editorial layout (Gucci / LV inspired).
 * - Generous whitespace, tall full-bleed cards
 * - Alternating 2-up "feature row" every 4 items
 * - Sticky minimal filter bar
 * - No card chrome — image speaks
 */
export function SectionPage({
  eyebrow,
  title,
  subtitle,
  filterTag,
  filterCategory,
  subFilters,
  subFilterKey = "subcategory",
}: Props) {
  const [sub, setSub] = useState<string>("All");
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", "active"],
    queryFn: () => fetchProducts(),
    placeholderData: keepPreviousData,
  });

  let list = products;
  if (filterTag) list = list.filter((p) => p.tags.includes(filterTag));
  if (filterCategory) list = list.filter((p) => p.category === filterCategory);
  if (sub !== "All") {
    if (subFilterKey === "subcategory") list = list.filter((p) => p.subcategory === sub);
    else list = list.filter((p) => p.tags.includes(sub));
  }

  return (
    <>
      {/* Editorial title block — tall, centered, generous breathing room */}
      <section className="pt-36 md:pt-48 pb-16 md:pb-20 container-luxe">
        <BackButton fallback="/shop" className="mb-8" />
        <div className="text-center">
          <div className="eyebrow mb-5 text-gold">{eyebrow}</div>
          <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-[5.5rem] tracking-tight leading-[1.05]">
            {title}
          </h1>
          <p className="mt-7 max-w-lg mx-auto text-sm md:text-base text-muted-foreground leading-relaxed font-light">
            {subtitle}
          </p>
          <p className="mt-6 text-[10px] uppercase tracking-[0.4em] text-muted-foreground/70">
            {list.length} {list.length === 1 ? "piece" : "pieces"}
          </p>
        </div>
      </section>

      {/* Sticky minimal filter rail */}
      {subFilters && subFilters.length > 0 && (
        <section className="sticky top-16 md:top-20 z-30 bg-background/90 backdrop-blur-md border-y border-border">
          <div className="container-luxe py-5">
            <div className="flex flex-wrap gap-x-8 gap-y-2 justify-center text-[10px] uppercase tracking-[0.32em]">
              {[{ label: "All", value: "All" }, ...subFilters].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setSub(f.value)}
                  className={`pb-1 border-b transition-colors ${
                    sub === f.value
                      ? "border-gold text-gold"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Editorial product grid — alternating wide pairs + standard rows */}
      <section className="container-luxe py-20 md:py-28">
        {isLoading ? (
          <MonogramLoader />
        ) : list.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-32 font-light tracking-wide">
            No pieces in this section yet.
          </div>
        ) : (
          <EditorialGrid products={list} />
        )}
      </section>
    </>
  );
}

/**
 * Editorial grid pattern (Gucci/LV style):
 * - Rhythm of 6: row of 2 (wide) → row of 4 (standard) → repeat
 * - Wide cards are ~50% width with taller aspect (3:4 vs 4:5)
 * - Massive vertical spacing, no borders, image-first
 */
function EditorialGrid({ products }: { products: ReturnType<typeof Array.prototype.slice> & any[] }) {
  // Group items into [pair (2), grid (4)] chunks of 6
  const chunks: { wide: typeof products; grid: typeof products }[] = [];
  for (let i = 0; i < products.length; i += 6) {
    chunks.push({
      wide: products.slice(i, i + 2),
      grid: products.slice(i + 2, i + 6),
    });
  }

  let runningIndex = 0;

  return (
    <div className="space-y-20 md:space-y-28">
      {chunks.map((chunk, ci) => (
        <div key={ci} className="space-y-20 md:space-y-28">
          {/* Wide editorial pair — 2 columns, large */}
          {chunk.wide.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 md:gap-x-10 gap-y-16">
              {chunk.wide.map((p) => {
                const i = runningIndex++;
                return (
                  <div key={p.slug} className="editorial-card">
                    <ProductCard product={p} index={i} eager={ci === 0 && i < 2} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Standard 4-up grid */}
          {chunk.grid.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 md:gap-x-8 gap-y-16">
              {chunk.grid.map((p) => {
                const i = runningIndex++;
                return <ProductCard key={p.slug} product={p} index={i} />;
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
